import type { RfqWorkspaceProps } from "./presentation/rfqs/rfq-workspace-types";
import { useRfqWorkspace } from "./presentation/rfqs/useRfqWorkspace";
import { RfqInvitationDialog } from "./presentation/rfqs/RfqInvitationDialog";
import { RfqComposeDialog } from "./presentation/rfqs/RfqComposeDialog";
import { RfqActions } from "./presentation/rfqs/RfqActions";
import { RfqCandidates } from "./presentation/rfqs/RfqCandidates";
import { RfqInvitations } from "./presentation/rfqs/RfqInvitations";
import { RfqCommunications } from "./presentation/rfqs/RfqCommunications";
import { RfqSecurityPanel } from "./presentation/rfqs/RfqSecurityPanel";
import { can } from "../../shared/auth/access";
import { statusLabels, methodLabels } from "./presentation/rfqs/rfq-presentation";

export function RfqWorkspace(props: RfqWorkspaceProps) {
  const { requestNo, user } = props;
  const key = JSON.stringify([requestNo, user.id, user.role, user.roles, user.permissions, user.dataScope, user.departmentId, user.preview, user.previewRole]);
  return <RfqRequestWorkspace key={key} {...props} />;
}

function RfqRequestWorkspace(props: RfqWorkspaceProps) {
  const {
    requestNo,
    title,
    category,
    quantity,
    unit,
    user,
    rfq,
    candidates,
    selected,
    loading,
    error,
    setSelected,
    reloadRfq,
    invitations,
    invitationLoading,
    invitationsLoaded,
    invitationError,
    loadInvitations,
    offers,
    evaluation,
    contractCompliance,
    communications,
    governanceError,
    loadGovernance,
    canManageInvitations,
    form,
    composeError,
    submitting,
    rfqCommandBusy,
    setForm,
    create,
    saveSelection,
    command,
    governanceBusy,
    deadlineExtension,
    setDeadlineExtension,
    unsealReason,
    setUnsealReason,
    announcementBody,
    setAnnouncementBody,
    answerDrafts,
    setAnswerDrafts,
    extendDeadline,
    unseal,
    evaluateUnifiedOffers,
    evaluateContract,
    createContractOrder,
    publishAnnouncement,
    answerQuestion,
    composeOpen,
    setComposeOpen,
    composeStep,
    invitationDrawerSupplierId,
    setInvitationDrawerSupplierId,
    invitationConfirm,
    setInvitationConfirm,
    workspaceRef,
    drawerRef,
    composeDrawerRef,
    composeTriggerRef,
    confirmationCancelRef,
    confirmationTriggerRef,
    confirmationActionRef,
    closeInvitationConfirmation,
    invitationMessage,
    testPortalUrl,
    invitationPending,
    testDeliveryLoading,
    invite,
    invitationCommand,
    loadTestPortalUrl,
    clearInvitationFeedback,
    closeInvitationDrawer,
    closeComposeDrawer,
    moveComposeStep,
    deadlinePreview,
    reviewDeadline,
    invitationSelection,
    drawerInvitation,
    requestDisplayNo,
    neededAtLabel,
  } = useRfqWorkspace(props);
  return (
    <>
      <section
        ref={workspaceRef}
        className="request-detail-section rfq-workspace"
        aria-labelledby="rfq-workspace-heading"
      >
        <header className="section-heading">
          <div>
            <span className="section-kicker">Teklif talebi</span>
            <h2 id="rfq-workspace-heading">Teklif Talebi çalışma alanı</h2>
          </div>
        </header>
        {loading ? (
          <p>Teklif Talebi kayıtları yükleniyor…</p>
        ) : error ? (
          <div className="error-state" role="alert">
            <span>{error}</span>
            <button className="secondary-button" onClick={() => void reloadRfq()}>
              Teklif talebini yenile
            </button>
          </div>
        ) : null}
        {!rfq && can(user, "rfq.manage") ? (
          <div className="table-state rfq-empty-state">
            <span className="section-kicker">Tedarikçi teklif süreci</span>
            <strong>Henüz Teklif Talebi oluşturulmadı</strong>
            <span>
              Seçili talep için kapsamı ve son tarihi belirleyerek tedarikçi
              davetini hazırlayın.
            </span>
            <button
              ref={composeTriggerRef}
              className="primary-button"
              disabled={loading || !!error}
              onClick={() => setComposeOpen(true)}
            >
              Teklif Talebi oluştur
            </button>
          </div>
        ) : null}
        {rfq ? (
          <>
            <div className="table-state rfq-summary">
              <strong>Teklif Talebi · {requestDisplayNo}</strong>
              <p>
                <span
                  className={`rfq-status rfq-status-${rfq.status.toLowerCase()}`}
                >
                  {statusLabels[rfq.status] ?? rfq.status}
                </span>
                <span>{methodLabels[rfq.method] ?? rfq.method}</span>
                <span>{rfq.timezone}</span>
              </p>
            </div>
            <RfqCandidates
              rfq={rfq}
              candidates={candidates}
              selected={selected}
              setSelected={setSelected}
            />
            <RfqActions
              user={user}
              rfq={rfq}
              rfqCommandBusy={rfqCommandBusy}
              saveSelection={saveSelection}
              command={command}
            />
            {rfq.status !== "DRAFT" ? (
              <RfqSecurityPanel
                user={user}
                rfq={rfq}
                offers={offers}
                evaluation={evaluation}
                contractCompliance={contractCompliance}
                governanceError={governanceError}
                loadGovernance={loadGovernance}
                governanceBusy={governanceBusy}
                deadlineExtension={deadlineExtension}
                setDeadlineExtension={setDeadlineExtension}
                unsealReason={unsealReason}
                setUnsealReason={setUnsealReason}
                extendDeadline={extendDeadline}
                unseal={unseal}
                evaluateUnifiedOffers={evaluateUnifiedOffers}
                evaluateContract={evaluateContract}
                createContractOrder={createContractOrder}
              />
            ) : null}
            {rfq.status !== "DRAFT" &&
            can(user, "supplier_portal.question.manage") ? (
              <RfqCommunications
                communications={communications}
                governanceBusy={governanceBusy}
                announcementBody={announcementBody}
                setAnnouncementBody={setAnnouncementBody}
                answerDrafts={answerDrafts}
                setAnswerDrafts={setAnswerDrafts}
                publishAnnouncement={publishAnnouncement}
                answerQuestion={answerQuestion}
              />
            ) : null}
            {rfq.status === "PUBLISHED" && canManageInvitations ? (
              <RfqInvitations
                rfq={rfq}
                invitations={invitations}
                invitationLoading={invitationLoading}
                invitationsLoaded={invitationsLoaded}
                invitationError={invitationError}
                loadInvitations={loadInvitations}
                invitationDrawerSupplierId={invitationDrawerSupplierId}
                setInvitationDrawerSupplierId={setInvitationDrawerSupplierId}
                clearInvitationFeedback={clearInvitationFeedback}
              />
            ) : null}
          </>
        ) : !loading && !can(user, "rfq.manage") ? (
          <p>Bu talep için Teklif Talebi bulunmuyor.</p>
        ) : null}
      </section>
      <RfqComposeDialog
        requestNo={requestNo}
        title={title}
        category={category}
        quantity={quantity}
        unit={unit}
        rfq={rfq}
        form={form}
        composeError={composeError}
        submitting={submitting}
        rfqCommandBusy={rfqCommandBusy}
        setForm={setForm}
        create={create}
        composeOpen={composeOpen}
        composeStep={composeStep}
        composeDrawerRef={composeDrawerRef}
        closeComposeDrawer={closeComposeDrawer}
        moveComposeStep={moveComposeStep}
        deadlinePreview={deadlinePreview}
        reviewDeadline={reviewDeadline}
        neededAtLabel={neededAtLabel}
      />
      <RfqInvitationDialog
        user={user}
        rfq={rfq}
        invitationLoading={invitationLoading}
        invitationsLoaded={invitationsLoaded}
        invitationError={invitationError}
        loadInvitations={loadInvitations}
        invitationDrawerSupplierId={invitationDrawerSupplierId}
        invitationConfirm={invitationConfirm}
        setInvitationConfirm={setInvitationConfirm}
        drawerRef={drawerRef}
        confirmationCancelRef={confirmationCancelRef}
        confirmationTriggerRef={confirmationTriggerRef}
        confirmationActionRef={confirmationActionRef}
        closeInvitationConfirmation={closeInvitationConfirmation}
        invitationMessage={invitationMessage}
        testPortalUrl={testPortalUrl}
        invitationPending={invitationPending}
        testDeliveryLoading={testDeliveryLoading}
        invite={invite}
        invitationCommand={invitationCommand}
        loadTestPortalUrl={loadTestPortalUrl}
        closeInvitationDrawer={closeInvitationDrawer}
        invitationSelection={invitationSelection}
        drawerInvitation={drawerInvitation}
      />
    </>
  );
}
